import { HttpContextToken, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, finalize, shareReplay } from 'rxjs';

/**
 * Marca uma requisição para **não** ser deduplicada. Use quando o reenvio idêntico for intencional
 * — por exemplo um "gerar de novo" que precisa mesmo produzir um segundo resultado.
 *
 * ```ts
 * this.http.post(url, body, { context: new HttpContext().set(SEM_DEDUP, true) });
 * ```
 */
export const SEM_DEDUP = new HttpContextToken<boolean>(() => false);

/**
 * Requisições idênticas ainda em voo. Injetável (e não um `Map` de módulo) para cada teste começar
 * limpo — estado de módulo vaza entre specs e produz falha que só aparece na ordem errada.
 */
@Injectable({ providedIn: 'root' })
export class RequisicoesEmVoo {
    private readonly emVoo = new Map<string, Observable<HttpEvent<unknown>>>();

    obter(chave: string): Observable<HttpEvent<unknown>> | undefined {
        return this.emVoo.get(chave);
    }

    registrar(chave: string, fluxo: Observable<HttpEvent<unknown>>): void {
        this.emVoo.set(chave, fluxo);
    }

    remover(chave: string): void {
        this.emVoo.delete(chave);
    }

    get tamanho(): number {
        return this.emVoo.size;
    }
}

/** Corpo que não dá para serializar de forma estável não entra na chave — a requisição passa direto. */
function corpoSerializavel(req: HttpRequest<unknown>): string | null {
    const corpo = req.body;
    if (corpo === null || corpo === undefined) {
        return '';
    }
    if (corpo instanceof FormData || corpo instanceof Blob || corpo instanceof ArrayBuffer) {
        return null;
    }
    try {
        return JSON.stringify(corpo);
    } catch {
        return null;
    }
}

/**
 * Colapsa requisições idênticas que estão em voo ao mesmo tempo: a segunda recebe o observable da
 * primeira em vez de disparar outra chamada.
 *
 * <p>Resolve o duplo clique em toda tela de escrita e as leituras concorrentes iguais, num lugar só
 * — o alternativo seria travar botão por botão, que é o que já vinha sendo feito à mão.</p>
 *
 * <p><b>Não confundir com idempotência.</b> Isto só cobre o intervalo em que a requisição está no
 * ar. Se a resposta se perdeu e o usuário reenvia depois, aqui não há nada em voo para colapsar e a
 * escrita chega duas vezes ao servidor. Essa parte depende da chave de idempotência no backend.</p>
 *
 * <p><b>Ordem na cadeia:</b> este interceptor precisa vir <b>antes</b> do de mensagem de erro. Os
 * interceptors registrados antes dele rodam uma vez por chamada; os registrados depois rodam uma vez
 * só, dentro do fluxo compartilhado. Se o de erro viesse antes, duas chamadas coalescidas gerariam
 * dois toasts para a mesma falha.</p>
 */
export const dedupRequisicoesInterceptor: HttpInterceptorFn = (req, next) => {
    const registro = inject(RequisicoesEmVoo);

    if (req.context.get(SEM_DEDUP)) {
        return next(req);
    }

    const corpo = corpoSerializavel(req);
    if (corpo === null) {
        return next(req);
    }

    const chave = `${req.method}|${req.urlWithParams}|${corpo}`;

    const existente = registro.obter(chave);
    if (existente) {
        return existente;
    }

    const fluxo = next(req).pipe(
        // `finalize` antes do `shareReplay`: precisa rodar no encerramento da fonte — sucesso, erro
        // ou cancelamento — e não quando o último inscrito sai do replay.
        finalize(() => registro.remover(chave)),
        // `refCount: true` para o cancelamento propagar quando ninguém mais escuta. Com `false` a
        // entrada ficaria quente segurando a resposta antiga.
        shareReplay({ bufferSize: 1, refCount: true })
    );

    registro.registrar(chave, fluxo);
    return fluxo;
};
